import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

declare global {
  interface Window {
    JitsiMeetExternalAPI?: new (
      domain: string,
      options: Record<string, any>
    ) => {
      dispose: () => void
      addEventListener: (event: string, cb: (data: any) => void) => void
      removeEventListener: (event: string, cb: (data: any) => void) => void
      executeCommand: (command: string, ...args: any[]) => void
    }
  }
}

interface JitsiMeetProps {
  roomName: string
  displayName?: string
  onClose?: () => void
  jwt?: string | null
}

const JITSI_APP_ID = 'vpaas-magic-cookie-2f3bd4934a914de8aa92382d16297991'
const JITSI_DOMAIN = '8x8.vc'
const JITSI_SCRIPT = `https://${JITSI_DOMAIN}/${JITSI_APP_ID}/external_api.js`

export default function JitsiMeet({ roomName, displayName, onClose, jwt }: JitsiMeetProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const apiRef = useRef<any>(null)

  // Sanitize room name: only alphanumeric, dash, underscore
  const safeRoom = roomName.replace(/[^a-zA-Z0-9-_]/g, '-')
  const jaasRoomName = `${JITSI_APP_ID}/${safeRoom}`

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let cancelled = false

    async function initJitsi() {
      // Load external_api.js if not already loaded
      if (!window.JitsiMeetExternalAPI) {
        await new Promise<void>((resolve, reject) => {
          const existing = document.querySelector(`script[src="${JITSI_SCRIPT}"]`)
          if (existing) {
            existing.addEventListener('load', () => resolve())
            existing.addEventListener('error', () => reject(new Error('Jitsi API load failed')))
            return
          }
          const script = document.createElement('script')
          script.src = JITSI_SCRIPT
          script.async = true
          script.onload = () => resolve()
          script.onerror = () => reject(new Error('Jitsi API load failed'))
          document.body.appendChild(script)
        })
      }

      if (cancelled || !container || !window.JitsiMeetExternalAPI) return

      // Clean container
      container.innerHTML = ''

      const options: Record<string, any> = {
        roomName: jaasRoomName,
        parentNode: container,
        width: '100%',
        height: '100%',
        userInfo: {
          displayName: displayName || 'Nyxeverix Guest',
        },
        configOverwrite: {
          prejoinPageEnabled: false,
          startWithAudioMuted: true,
          startWithVideoMuted: true,
          disableDeepLinking: true,
          enableWelcomePage: false,
          enableClosePage: false,
          hideLobbyButton: false,
          enableLobby: true,
          brandingRoomAlias: safeRoom,
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_BRAND_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          TOOLBAR_BUTTONS: [
            'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
            'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
            'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
            'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
            'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone',
            'security', 'lobby-button'
          ],
        },
      }

      // JWT enables moderator rights, recording, dial-out etc.
      if (jwt) {
        options.jwt = jwt
      }

      const api = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, options)

      apiRef.current = api

      // Notify on hangup
      api.addEventListener('readyToClose', () => {
        onClose?.()
      })

      // Track moderator status via JWT
      api.addEventListener('participantRoleChanged', (event: any) => {
        if (event.role === 'moderator') {
          console.log('[Jitsi] Moderator privileges acquired')
        }
      })

      // Log when recording becomes available
      api.addEventListener('recordingStatusChanged', (event: any) => {
        console.log('[Jitsi] Recording status:', event.on ? 'started' : 'stopped')
      })
    }

    initJitsi()

    return () => {
      cancelled = true
      try {
        apiRef.current?.dispose()
        apiRef.current = null
      } catch {
        // ignore
      }
    }
  }, [jaasRoomName, safeRoom, displayName, jwt, onClose])

  return (
    <div className="w-full rounded-xl overflow-hidden border border-border bg-black shadow-lg flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-card border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-medium text-muted-foreground">
            {t('events.jitsiRoom')}: {safeRoom}
          </span>
          {jwt && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
              {t('events.jitsiModerator')}
            </span>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={() => {
              try { apiRef.current?.executeCommand('hangup') } catch {}
              onClose()
            }}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Jitsi container */}
      <div ref={containerRef} className="w-full h-[500px] md:h-[600px] lg:h-[700px]" />
    </div>
  )
}
